import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

export default class ScrollAnimations {
  constructor(experience, smoothScroll) {
    this.experience = experience;
    this.lenis = smoothScroll.getLenis();

    // Register ScrollTrigger plugin
    gsap.registerPlugin(ScrollTrigger);

    // Make ScrollTrigger available globally for other components
    window.ScrollTrigger = ScrollTrigger;

    this.init();
  }

  init() {
    // Connect Lenis with ScrollTrigger
    this.lenis.on("scroll", ScrollTrigger.update);

    // Setup parallax when experience is ready
    this.experience.resources.on("ready", () => {
      this.setupParallax();
    });
  }

  setupParallax() {
    // Setup parallax for hero crystals only
    if (this.experience.world?.heroCrystals) {
      ScrollTrigger.create({
        trigger: "body",
        start: "top top",
        end: "bottom bottom",
        scrub: 2,
        onUpdate: (self) => {
          if (this.experience.world.heroCrystals) {
            this.experience.world.heroCrystals.setParallaxOffset(self.progress);
          }
        },
      });
    }

    // Setup 3-part AboutMiiHead animation system
    if (this.experience.world?.aboutMiiHead) {
      // Part 1: Entrance during About section
      ScrollTrigger.create({
        trigger: "#about",
        start: "top 85%", // More sensitive - start animation earlier
        end: "top 10%",   // Longer animation range for more smoothness
        scrub: 3,         // Original scrub value
        onUpdate: (self) => {
          if (this.experience.world.aboutMiiHead) {
            // Apply easing
            const easedProgress = gsap.parseEase("power1.out")(self.progress);
            this.experience.world.aboutMiiHead.setScrollProgress("entrance", easedProgress);
          }
        },
      });

      // Part 2: Move to center during Skills section (before pin starts)
      ScrollTrigger.create({
        trigger: "#skills",
        start: "top 60%", // Start moving to center when skills section approaches
        end: "top top",   // Complete center transition when pin starts
        scrub: 5,         // Increased from 1 to 5 for smoother center movement
        onUpdate: (self) => {
          if (this.experience.world.aboutMiiHead) {
            // Apply smooth easing for confident center movement
            const easedProgress = gsap.parseEase("power2.inOut")(self.progress);
            this.experience.world.aboutMiiHead.setScrollProgress("toCenter", easedProgress);
          }
        },
      });

      // Part 3: Exit upward - start during pin, finish when pin ends
      ScrollTrigger.create({
        trigger: "#skills",
        start: "bottom 101%", // Start exit when there's 1% of pin left
        end: "bottom top",    // Complete exit exactly when pin ends
        scrub: 5,             // Smooth animation
        onUpdate: (self) => {
          if (this.experience.world.aboutMiiHead) {
            // Apply accelerating easing for dramatic upward exit
            const easedProgress = gsap.parseEase("power3.in")(self.progress);
            this.experience.world.aboutMiiHead.setScrollProgress("exit", easedProgress);
          }
        },
      });

      // Pin the Skills section for extended engagement
      ScrollTrigger.create({
        trigger: "#skills",
        start: "top top",
        end: "+=80%", // Pin for 150% of viewport height (adjust as needed)
        pin: true,
        anticipatePin: 1,
        scrub: false
      });

      // Contact section - clean slate for now

      window.addEventListener("resize", () => {
        ScrollTrigger.refresh();
      });
    }
  }

  destroy() {
    // Clean up ScrollTrigger
    ScrollTrigger.getAll().forEach((trigger) => trigger.kill());
    this.lenis.off("scroll", ScrollTrigger.update);
  }
}
